import { useCallback, useEffect, useId } from "react"

import { appStore } from "../store/AppStore"
import { Buttons, keydown } from "../utilities/constants"
import { useRouteMatch } from "../utilities/use-route-match"

import { useEventBind } from "./useEventBind"

/**
 * It is important to register this hook inside a conditional component.
 * or else the BB override will register it as visible while it's still not loaded into view.
 */
export const useModalDismiss = (
  callback: () => void,
  dismissible = true,
  useRoute = false,
  onDeferredBackPress?: () => void,
) => {
  const registerBackButtonOverride = appStore(
    (state) => state.registerBackButtonOverride,
  )
  const isMyRoute = useRouteMatch()
  const backButtonOverride = appStore((state) => state.backButtonOverride)
  const id = useId()

  // useRoute ensures that the override remains active until the page reroutes
  useEffect(() => {
    if (useRoute) {
      registerBackButtonOverride(id, isMyRoute)
    }
  }, [isMyRoute])

  const keyListener = useCallback(
    (e: KeyboardEvent) => {
      if (
        (!useRoute || isMyRoute) &&
        e.keyCode === Buttons.Back &&
        backButtonOverride[backButtonOverride.length - 1] === id
      ) {
        e.stopImmediatePropagation()
        if (dismissible) {
          callback()
        } else {
          if (onDeferredBackPress) onDeferredBackPress()
        }
      }
    },
    [callback, dismissible, useRoute, isMyRoute, backButtonOverride],
  )

  useEventBind(window, keydown, keyListener)

  useEffect(() => {
    registerBackButtonOverride(id, true)
    return () => {
      registerBackButtonOverride(id, false)
    }
  }, [])
}
